import { useState, useEffect, useCallback } from 'react';
import { Session } from '../types/multiTenant';
import sessionService from '../services/sessionService';
import { useOrganization } from '../contexts/OrganizationContext';

interface SessionFilters {
  clientId?: string;
  conductedBy?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Hook for managing sessions
 * Automatically loads sessions for the current organization
 */
export const useSessions = (filters?: SessionFilters) => {
  const { organization, currentUser } = useOrganization();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load sessions with filters
   */
  // Dependency em organization?.id (primitivo) em vez do objeto inteiro.
  // Ver explicação detalhada em useClients.ts e OrganizationContext.tsx.
  const loadSessions = useCallback(async () => {
    if (!organization) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await sessionService.listSessions(organization.id, filters);
      setSessions(data);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id, filters]);

  /**
   * Create new session
   */
  const createSession = useCallback(
    async (data: Omit<Parameters<typeof sessionService.createSession>[0], 'organizationId' | 'createdBy'>) => {
      if (!organization || !currentUser) {
        setError('Organization or user not loaded');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const newSession = await sessionService.createSession({
          ...data,
          organizationId: organization.id,
          createdBy: currentUser.id,
        });

        if (newSession) {
          setSessions((prev) => [newSession, ...prev]);
        }

        return newSession;
      } catch (err) {
        console.error('Error creating session:', err);
        setError('Failed to create session');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [organization, currentUser]
  );

  /**
   * Update session
   */
  const updateSession = useCallback(
    async (sessionId: string, updates: Partial<Session>) => {
      setIsLoading(true);
      setError(null);

      try {
        const updated = await sessionService.updateSession(sessionId, updates);

        if (updated) {
          setSessions((prev) =>
            prev.map((session) => (session.id === sessionId ? updated : session))
          );
        }

        return updated;
      } catch (err) {
        console.error('Error updating session:', err);
        setError('Failed to update session');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Delete session
   */
  const deleteSession = useCallback(async (sessionId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const success = await sessionService.deleteSession(sessionId);

      if (success) {
        setSessions((prev) => prev.filter((session) => session.id !== sessionId));
      }

      return success;
    } catch (err) {
      console.error('Error deleting session:', err);
      setError('Failed to delete session');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get sessions by client
   */
  const getSessionsByClient = useCallback(async (clientId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await sessionService.getSessionsByClient(clientId);
      return data;
    } catch (err) {
      console.error('Error getting sessions by client:', err);
      setError('Failed to get sessions');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Search sessions
   */
  const searchSessions = useCallback(
    async (query: string) => {
      if (!organization) return [];

      setIsLoading(true);
      setError(null);

      try {
        const results = await sessionService.searchSessions(organization.id, query);
        return results;
      } catch (err) {
        console.error('Error searching sessions:', err);
        setError('Failed to search sessions');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [organization]
  );

  /**
   * Get sessions by tag
   */
  const getSessionsByTag = useCallback(
    async (tag: string) => {
      if (!organization) return [];

      setIsLoading(true);
      setError(null);

      try {
        const results = await sessionService.getSessionsByTag(organization.id, tag);
        return results;
      } catch (err) {
        console.error('Error getting sessions by tag:', err);
        setError('Failed to get sessions by tag');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [organization]
  );

  /**
   * Get session statistics
   */
  const getSessionStats = useCallback(
    async (startDate?: string, endDate?: string) => {
      if (!organization) return null;

      try {
        const stats = await sessionService.getSessionStats(organization.id, startDate, endDate);
        return stats;
      } catch (err) {
        console.error('Error getting session stats:', err);
        return null;
      }
    },
    [organization]
  );

  /**
   * Refresh sessions
   */
  const refresh = useCallback(() => {
    loadSessions();
  }, [loadSessions]);

  // Auto-load on mount and when organization or filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (organization?.id) {
      loadSessions();
    }
  }, [organization?.id, filters]);

  return {
    // Data
    sessions,
    isLoading,
    error,

    // Actions
    createSession,
    updateSession,
    deleteSession,
    getSessionsByClient,
    searchSessions,
    getSessionsByTag,
    getSessionStats,
    refresh,
  };
};

export default useSessions;
